import React, { useCallback, useEffect, useState } from "react";
import { Wallet, Plus, Receipt, ArrowDownLeft, ArrowUpRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Spinner, EmptyState } from "../../../components/common";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { api, errMsg, fmtINR, fmtDateTime } from "../../../lib/api";
import { modeLabel } from "../../../components/parking/common";
import { PayModePicker } from "../../../components/parking/PayFlow";
import { ReceiptDialog } from "../../../components/parking/Ticket";

/* Phase 201 — Wallet (top-up, refunds, history) + all parking receipts. */

const TOPUPS = [100, 200, 500, 1000];

export function WalletTab({ ctx }) {
  const { code, live, startPay, refresh } = ctx;
  const [wallet, setWallet] = useState(null);
  const [receipts, setReceipts] = useState(null);
  const [topup, setTopup] = useState(false);
  const [receipt, setReceipt] = useState(null);

  const load = useCallback(() => {
    api.get(`/t/${code}/parking/citizen/wallet`).then((r) => setWallet(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get(`/t/${code}/parking/citizen/receipts`).then((r) => setReceipts(r.data)).catch(() => {});
  }, [code]);
  useEffect(() => { load(); }, [load, live]);

  const openReceipt = (r) => api.get(`/t/${code}/parking/citizen/receipts/${r.id}`).then((x) => setReceipt(x.data)).catch((e) => toast.error(errMsg(e)));

  if (!wallet) return <Spinner />;
  return (
    <div className="space-y-4" data-testid="citizen-parking-wallet">
      <Card className="shadow-sm overflow-hidden">
        <CardContent className="p-5 flex items-center justify-between gap-4 flex-wrap bg-primary/5">
          <div><p className="text-xs text-muted-foreground flex items-center gap-1.5"><Wallet className="h-3.5 w-3.5" /> Parking wallet</p><p className="text-3xl font-semibold tabular-nums font-heading" data-testid="wallet-balance">{fmtINR(wallet.balance)}</p><p className="text-xs text-muted-foreground mt-0.5">Pay instantly at booking; cancellations are refunded here.</p></div>
          <Button className="h-11" onClick={() => setTopup(true)} data-testid="wallet-topup-button"><Plus className="h-4 w-4 mr-1.5" /> Add money</Button>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="shadow-sm">
          <CardHeader className="pb-2"><CardTitle className="text-base font-heading">Wallet history</CardTitle></CardHeader>
          <CardContent>
            {wallet.transactions.length === 0 ? <p className="text-sm text-muted-foreground">No wallet transactions yet.</p> : (
              <div className="space-y-2" data-testid="wallet-transactions">
                {wallet.transactions.map((t) => (
                  <div key={t.id} className="flex items-center justify-between gap-3 text-sm rounded-lg border px-3 py-2" data-testid={`wallet-txn-${t.id}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`h-8 w-8 rounded-full inline-flex items-center justify-center shrink-0 ${t.kind === "credit" ? "bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]" : "bg-muted text-muted-foreground"}`}>{t.kind === "credit" ? <ArrowDownLeft className="h-4 w-4" /> : <ArrowUpRight className="h-4 w-4" />}</span>
                      <div className="min-w-0"><p className="font-medium truncate capitalize">{t.note || t.ref_kind || t.kind}</p><p className="text-xs text-muted-foreground">{fmtDateTime(t.created_at)}</p></div>
                    </div>
                    <div className="text-right shrink-0"><p className={`tabular-nums font-semibold ${t.kind === "credit" ? "text-[hsl(var(--success))]" : ""}`}>{t.kind === "credit" ? "+" : "−"}{fmtINR(t.amount)}</p><p className="text-[11px] text-muted-foreground">Bal {fmtINR(t.balance_after)}</p></div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardHeader className="pb-2"><CardTitle className="text-base font-heading flex items-center gap-2"><Receipt className="h-4 w-4 text-primary" /> Receipts</CardTitle></CardHeader>
          <CardContent>
            {!receipts ? <Spinner /> : receipts.length === 0 ? <EmptyState title="No receipts yet" subtitle="Every parking payment lands here." /> : (
              <div className="space-y-2" data-testid="receipts-list">
                {receipts.map((r) => (
                  <button key={r.id} type="button" onClick={() => openReceipt(r)} data-testid={`receipt-row-${r.receipt_no}`}
                    className="w-full flex items-center justify-between gap-3 text-sm rounded-lg border px-3 py-2 text-left hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                    <div className="min-w-0"><p className="font-mono text-xs">{r.receipt_no}</p><p className="text-xs text-muted-foreground truncate capitalize">{String(r.kind).replace(/_/g, " ")} · {r.location_name || "—"} · {r.vehicle_number || ""}</p><p className="text-[11px] text-muted-foreground">{fmtDateTime(r.paid_at)} · {modeLabel(r.mode)}</p></div>
                    <p className="tabular-nums font-semibold shrink-0">{fmtINR(r.amount)}</p>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {topup && <TopupDialog code={code} onClose={() => setTopup(false)} onOrder={(order, amt) => { setTopup(false); startPay(order, { title: "Add money to wallet", lines: [{ label: "Top-up", value: fmtINR(amt) }], onPaid: (res) => { toast.success(`Wallet topped up — balance ${fmtINR(res.wallet_balance)}`); refresh(); load(); } }); }} />}
      <ReceiptDialog receipt={receipt} onClose={() => setReceipt(null)} />
    </div>
  );
}

function TopupDialog({ code, onClose, onOrder }) {
  const [amount, setAmount] = useState(200);
  const [mode, setMode] = useState("upi");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { const r = await api.post(`/t/${code}/parking/citizen/wallet/topup`, { amount: Number(amount), payment_mode: mode }); onOrder(r.data, Number(amount)); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="topup-dialog">
        <DialogHeader><DialogTitle className="font-heading">Add money</DialogTitle><DialogDescription>Between {fmtINR(10)} and {fmtINR(10000)}. Pay with UPI or card.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">{TOPUPS.map((a) => <button key={a} type="button" onClick={() => setAmount(a)} data-testid={`topup-${a}`} className={`rounded-full border px-3 py-2 text-sm min-h-[40px] tabular-nums ${Number(amount) === a ? "border-primary bg-primary text-primary-foreground" : "hover:bg-accent"}`}>{fmtINR(a)}</button>)}</div>
          <div><Label htmlFor="topup-amount" className="text-xs">Amount (₹)</Label><Input id="topup-amount" type="number" min={10} max={10000} value={amount} onChange={(e) => setAmount(e.target.value)} className="h-11 mt-1" data-testid="topup-amount" /></div>
          <div><Label className="text-xs">Pay with</Label><div className="mt-1"><PayModePicker modes={["upi", "card", "netbanking"]} value={mode} onChange={setMode} testId="topup-pay-mode" /></div></div>
        </div>
        <DialogFooter className="gap-2 sm:gap-0"><Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button><Button className="h-11" onClick={go} disabled={busy || Number(amount) < 10 || Number(amount) > 10000} data-testid="topup-submit">{busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-1.5" />} Add {fmtINR(amount)}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
